import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'

import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import { Button, Modal, Progress } from 'antd'
import type { UploadFile } from 'antd/es/upload'
import { useRolodexEdit } from 'features/fde'
import { useRolodexSignedURLs } from 'features/rolodex'
import { type FC, useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { GeckoEntry, RolodexEntryTagsType } from 'types'

import { DocumentList } from './components/DocumentList'
import { DocumentPreview } from './components/DocumentPreview'
import { FileUploadDragger } from './components/FileUploadDragger'
import { useLinkedDocuments } from './hooks/useDocuments'
import { RolodexDocumentEntry } from './types'
import { isImageDocument, isPdfDocument } from './utils/DocumentUtils'
import { uploadFiles } from './utils/fileUploadUtils'

interface FileUploaderProps {
  beforeUpload?: () => Promise<void>
  onUploadComplete?: (documentIds: ReadonlyArray<string>) => void
  onUploadError?: (error: string) => void
  maxFileSize?: number
  acceptedFileTypes?: ReadonlyArray<string>
  multiple?: boolean
  disabled?: boolean
  className?: string
  tags: RolodexEntryTagsType
  entry: GeckoEntry
  showExistingFiles?: boolean
  condensed?: boolean
}

const Container = styled.div`
  width: 100%;
`

const UploadButton = styled(Button)`
  margin-top: 1rem;
  font-weight: 600;
  height: 2.5rem;
  border-radius: 0.5rem;
`

const ProgressContainer = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  background: ${CantileverTheme.colors.grayscale[100]};
  border-radius: 0.5rem;
  border: 0.0625rem solid ${CantileverTheme.colors.grayscale[300]};
`

const ProgressText = styled.p`
  margin-top: 0.5rem;
  color: ${CantileverTheme.colors.grayscale[500]};
  font-size: 0.75rem;
`

/**
 * A component which assists with uploading files documents and linking them to
 * a given entry.
 *
 * It provides a preview of the uploaded PDF files.
 *
 * It provides a list of existing documents linked to the entry.
 */
export const FileUploader: FC<FileUploaderProps> = ({
  onUploadComplete,
  onUploadError,
  maxFileSize = 20,
  acceptedFileTypes = ['*/*'],
  multiple = true,
  disabled,
  className,
  tags = {},
  entry,
  showExistingFiles = true,
  condensed = false,
  beforeUpload,
}) => {
  const [fileList, setFileList] = useState<ReadonlyArray<UploadFile>>([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<
    Readonly<Record<string, number>>
  >({})
  const [previewFile, setPreviewFile] = useState<UploadFile | null>(null)

  const { rolodexPut, rolodexUploadFile } = useRolodexEdit()

  const linkedDocumentTags = useMemo<RolodexEntryTagsType>(
    () => ({
      [entry.type]: entry.id,
    }),
    [entry.id, entry.type]
  )

  const { documents: existingDocuments, forceRefresh: forceRefreshDocuments } =
    useLinkedDocuments(showExistingFiles ? linkedDocumentTags : undefined)

  const previewableDocuments = existingDocuments.filter((doc) => {
    return isPdfDocument(doc) || isImageDocument(doc)
  })
  const signedUrlQuery = useMemo(() => {
    return previewableDocuments.map((doc) => ({
      entry_id: doc.entry_id,
      path: doc.filename || '',
    }))
  }, [previewableDocuments])

  const { urls: existingFileUrls } = useRolodexSignedURLs(signedUrlQuery)

  // Create a map of document entryIds to their signed URLs
  const documentUrlMap = useMemo<Readonly<Record<string, string>>>(() => {
    return previewableDocuments.reduce<Record<string, string>>(
      (urlMap, doc, index) => {
        if (existingFileUrls[index]) {
          urlMap[doc.entry_id] = existingFileUrls[index]
        }
        return urlMap
      },
      {}
    )
  }, [previewableDocuments, existingFileUrls])

  // Convert previewFile to Document format for DocumentPreview component
  const previewDocument = useMemo((): RolodexDocumentEntry | null => {
    if (!previewFile) return null

    const document = existingDocuments.find(
      (doc) => doc.entry_id === previewFile.uid
    )
    if (!document) return null

    return document
  }, [previewFile, existingDocuments])

  const handleUpload = useCallback(async () => {
    setUploading(true)
    await beforeUpload?.()
    try {
      await uploadFiles(
        fileList,
        entry,
        tags,
        rolodexPut,
        rolodexUploadFile,
        onUploadComplete,
        onUploadError,
        forceRefreshDocuments,
        setUploadProgress
      )
    } finally {
      setUploading(false)
      setUploadProgress({})
      setFileList([])
    }
  }, [
    beforeUpload,
    fileList,
    entry,
    tags,
    rolodexPut,
    rolodexUploadFile,
    onUploadComplete,
    onUploadError,
    forceRefreshDocuments,
  ])

  const handleFileListChange = useCallback(
    (newFileList: ReadonlyArray<UploadFile>) => {
      setFileList(newFileList)
    },
    []
  )

  const hasFiles = fileList.length > 0
  const hasProgress = Object.keys(uploadProgress).length > 0

  const handleDocumentSelect = useCallback(
    (documentId: string) => {
      const document = existingDocuments.find(
        (doc) => doc.entry_id === documentId
      )
      if (document) {
        const isPdf = isPdfDocument(document)
        const isImage = isImageDocument(document)
        const hasUrl = documentId in documentUrlMap
        if ((isPdf || isImage) && hasUrl) {
          setPreviewFile({
            uid: documentId,
            name: document.filename || '',
            type: document.file_type || '',
            status: 'done',
            size: document.size,
          })
        }
      }
    },
    [existingDocuments, documentUrlMap]
  )

  return (
    <Container className={className}>
      <FileUploadDragger
        uploading={uploading}
        fileList={fileList}
        onFileListChange={handleFileListChange}
        maxFileSize={maxFileSize}
        acceptedFileTypes={acceptedFileTypes}
        multiple={multiple}
        disabled={disabled || uploading}
        className={className}
        condensed={condensed}
      />

      {hasFiles && !uploading && (
        <>
          <UploadButton
            type='primary'
            size={condensed ? 'small' : undefined}
            onClick={handleUpload}
            disabled={uploading}
            loading={uploading}
            icon={<FileTextOutlined />}
            block>
            {uploading
              ? 'Uploading...'
              : `Upload ${fileList.length} File${fileList.length > 1 ? 's' : ''}`}
          </UploadButton>
        </>
      )}

      {hasProgress && uploading && (
        <ProgressContainer>
          <Progress
            percent={Math.round(
              Object.values(uploadProgress).reduce((sum, val) => sum + val, 0) /
                Object.keys(uploadProgress).length
            )}
            status='active'
          />
          <ProgressText>Uploading files...</ProgressText>
        </ProgressContainer>
      )}

      {showExistingFiles && existingDocuments.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <DocumentList
            documents={existingDocuments}
            selectedDocumentId={previewDocument?.entry_id || null}
            onDocumentSelect={handleDocumentSelect}
          />
        </div>
      )}

      {previewFile && (
        <Modal
          open={!!previewFile}
          onCancel={() => setPreviewFile(null)}
          footer={null}
          width={800}
          title={previewFile.name}
          style={{ maxWidth: '90vw' }}>
          <div
            style={{
              width: '100%',
              maxHeight: '70vh',
              margin: '0 auto',
            }}>
            <DocumentPreview document={previewDocument} />
          </div>
        </Modal>
      )}
    </Container>
  )
}
